import { apiClient } from '@/auth';

export type PatientReportMetric = {
  metric: string;
  value?: number;
  date?: string;
};

/**
 * From PatientReportSecureLinkResponse (swagger.yaml:15443). The earlier
 * placeholder had `report_id` + `title` which don't exist on the real
 * response — the real shape uses `patient_report_id` + a date range.
 */
export type NutritionAggVal = {
  sum?: number;
  avg?: number;
  median?: number;
  min?: number;
  max?: number;
};

export type NutritionDetailsRow = {
  meal_id?: number | null;
  meal_name?: string | null;
  meal_date?: string | null;
  meal_date_count: number;
  meal_count: number;
  meal_item_count: number;
  // Each nutrient appears as both `<key>_sum: number` and `<key>: NutritionAggVal`.
  // We index dynamically by string at the call site since the schema enumerates
  // dozens of fields.
  [k: string]: number | string | null | undefined | NutritionAggVal;
};

export type NutritionDetailsTablePage = {
  rows: NutritionDetailsRow[];
  has_more: boolean;
  next?: string;
  group_by?: string[];
};

export type ReportSecureLink = {
  patient_id: number;
  patient_report_id: number;
  patient_subscription_id?: number;
  url: string;
  report_sent_time?: string;
  date_since: string;
  date_until: string;
};

export type ClinicalSummaryRow = {
  id: number;
  category: string;
  sub_category: string;
  type: 'positive' | 'negative' | 'na';
  text: string;
};

export type ClinicalSummaryResponse = {
  date_since: string;
  date_until: string;
  dates_exclude?: string[] | null;
  results: ClinicalSummaryRow[];
};

/**
 * Per-meal food-group breakdown for the patient's average plate. Each
 * meal-name bucket carries the four food group fractions; values typically
 * sum to ~1.0 representing percent of plate. MNT swagger:19115.
 */
export type AveragePlateMeal = {
  protein: number | null;
  fruit: number | null;
  grains: number | null;
  vegetable: number | null;
};

export type AveragePlateData = {
  date_since?: string;
  date_until?: string;
  calculation_method: string;
  breakfast?: AveragePlateMeal | null;
  lunch?: AveragePlateMeal | null;
  dinner?: AveragePlateMeal | null;
  snack?: AveragePlateMeal | null;
  overall?: AveragePlateMeal | null;
  statements?: string[] | null;
  n_meals?: number | null;
};

/**
 * Subset of MNT's `PatientReportResponse` (swagger:15467) — full schema is
 * 30+ fields, we only use what the Reports tab needs to group + display.
 *
 * Critically, this is the only endpoint that returns `status` and
 * `created_by_user_id`, which v1 uses to bucket reports into Clinician
 * Generated / RxFood Generated / Sent (clinician/src/pages/patient-reports.tsx:269).
 */
export type PatientReport = {
  patient_id: number;
  patient_report_id: number;
  hospital_id: number;
  clinic_id: number;
  clinic_name?: string;
  status?: string;
  report_type?: string;
  date_since: string;
  date_until: string;
  report_date?: string;
  report_sent_time?: string;
  created_by_user_id?: number | null;
  created_time?: string;
  updated_time?: string;
};

export const reportsApi = {
  /**
   * POST not GET — see swagger.yaml:617. Required query params: date_since, date_until.
   * 403 means the clinician's `clinician_can_generate_reports` flag is off; we
   * treat it as "no metrics" so the surrounding tab still renders.
   */
  async metrics(params: { date_since: string; date_until: string }): Promise<PatientReportMetric[]> {
    try {
      const res = await apiClient.post<PatientReportMetric[]>(`/reporting/patient_report_metrics`, undefined, {
        params,
      });
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return [];
      throw err;
    }
  },

  /**
   * Per-meal-or-per-day nutrient breakdown. v1 uses this for the Nutrients tab
   * (clinician/src/pages/patient-nutrient-new.tsx). Each row carries `<nutrient>_sum`
   * fields plus `meal_date_count` / `meal_count` aggregates we use for averages.
   * Verified swagger.yaml:946.
   */
  async nutritionDetailsTable(
    patientId: number,
    params: {
      /** Comma-separated list per MNT `get_groupby_columns` (api.py:268).
       *  Pass a single grouper for top-line aggregates ('meal_name',
       *  'meal_date') or multiple for the drilldown ('meal_id,meal_date,
       *  meal_name') so each row carries the metadata callers need without
       *  a second fetch. */
      group_by?: string;
      date_since?: string;
      date_until?: string;
      /**
       * Optional contiguous-range chunks. When provided (typically because
       * the clinician unchecked some days in the day-filter on the Nutrients
       * tab), the API filter is built as `meal_date.or: [{gte,lte}, ...]`
       * with one entry per chunk. Mirrors v1's behavior in foodFilterNew.tsx.
       * When omitted, falls back to the single-range `date_since`/`date_until`.
       */
      date_ranges?: Array<[string, string]>;
    },
  ): Promise<NutritionDetailsTablePage> {
    try {
      const orChunks =
        params.date_ranges && params.date_ranges.length > 0
          ? params.date_ranges.map(([gte, lte]) => ({ gte, lte }))
          : params.date_since && params.date_until
            ? [{ gte: params.date_since, lte: params.date_until }]
            : null;
      const res = await apiClient.get<NutritionDetailsTablePage>(
        `/reporting/patients/${patientId}/nutrition_details_table`,
        {
          params: {
            group_by: params.group_by,
            // Endpoint takes `filter` as a JSON-stringified object; v1 wraps
            // the date range(s) in `meal_date.or: [{gte, lte}, ...]`.
            filter: orChunks ? JSON.stringify({ meal_date: { or: orChunks } }) : undefined,
          },
        },
      );
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) {
        return { rows: [], has_more: false };
      }
      throw err;
    }
  },

  /**
   * Download a PDF of the patient's full meal history for the date range.
   * Mirrors v1's PatientService.downloadMealReport (clinician/src/services/patient.ts:1108).
   * Endpoint requires the requesting clinician have `is_meal_admin: true`;
   * callers should gate the UI before invoking this.
   *
   * Returns a Blob so the caller can build a download link. The endpoint
   * responds 404 if there's no qualifying data in the range; we surface that
   * by re-throwing — the UI shows a "no data in range" toast.
   */
  async downloadMealReport(input: {
    clinicianId: number;
    patientId: number;
    dateSince: string;
    dateUntil: string;
  }): Promise<Blob> {
    const { clinicianId, patientId, dateSince, dateUntil } = input;
    const res = await apiClient.get<Blob>(
      `/reporting/data_reviewer/${clinicianId}/patient/${patientId}/meal_report`,
      {
        params: { date_since: dateSince, date_until: dateUntil },
        responseType: 'blob',
      },
    );
    return res.data;
  },

  async secureLinks(patientId: number): Promise<ReportSecureLink[]> {
    try {
      const res = await apiClient.get<ReportSecureLink[]>(`/reporting/patients/${patientId}/food_reports/secure_links`);
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      if ((err as { response?: { status?: number } }).response?.status === 404) return [];
      throw err;
    }
  },

  /**
   * Clinical summary for a date range — positives/negatives statements that
   * v1 surfaces inline on the Reports tab. Verified swagger.yaml:5989.
   */
  async clinicalSummary(
    patientId: number,
    params: { date_since: string; date_until: string; disaggregated?: boolean },
  ): Promise<ClinicalSummaryResponse | null> {
    try {
      const res = await apiClient.get<ClinicalSummaryResponse>(`/patients/${patientId}/clinical_summary`, {
        params: {
          date_since: params.date_since,
          date_until: params.date_until,
          disaggregated: params.disaggregated ?? false,
        },
      });
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return null;
      throw err;
    }
  },

  /**
   * Auto-generation entrypoint. Triggers the system to create a report
   * automatically (used by background pipelines). Distinct from `createDraft`
   * below, which is what the clinician-facing "Generate new report" button
   * needs to call.
   */
  async autoGenerate(patientId: number) {
    const res = await apiClient.post(`/reporting/patient_report_auto_generation`, { patient_id: patientId });
    return res.data;
  },

  /**
   * Manually generate a draft (or sent) PDF report for a patient. Mirrors
   * v1's `postFoodReport` (clinician/src/services/patient.ts:233) which is
   * what the existing clinician portal's "Generate new report" button calls.
   *
   * Endpoint: POST `/hospital/{h}/clinic/{c}/patient/{p}/food_report`
   * (MNT swagger:9804). All path components are required; `clinic_id` must
   * be one the patient AND clinician both belong to. Query params:
   *   - date_since, date_until (required)
   *   - status: 'draft' | 'sent' (required)
   *   - as_background_task: defaults to true so the request returns
   *     immediately and the clinician can keep working while the PDF builds.
   *
   * The previous V2 implementation called `/reporting/patient_report_auto_generation`
   * — that's the system auto-gen endpoint, not the clinician trigger, which
   * is why "Generate new report" returned an error.
   */
  async createDraft(input: {
    patientId: number;
    hospitalId: number;
    clinicId: number;
    dateSince: string;
    dateUntil: string;
    status?: 'draft' | 'sent';
    patientSubscriptionId?: number | null;
    datesExclude?: string[];
    /**
     * When provided, the server generates a comparison report contrasting
     * the new range with this older report. v1 always sends both
     * `comparison_report_id` and `is_comparison_report=true` together
     * (clinician/src/services/patient.ts).
     */
    comparisonReportId?: number | null;
    asBackgroundTask?: boolean;
  }) {
    const {
      patientId,
      hospitalId,
      clinicId,
      dateSince,
      dateUntil,
      status = 'draft',
      patientSubscriptionId,
      datesExclude,
      comparisonReportId,
      asBackgroundTask = true,
    } = input;
    const res = await apiClient.post(
      `/hospital/${hospitalId}/clinic/${clinicId}/patient/${patientId}/food_report`,
      undefined,
      {
        params: {
          date_since: dateSince,
          date_until: dateUntil,
          status,
          patient_subscription_id: patientSubscriptionId ?? undefined,
          dates_exclude: datesExclude && datesExclude.length > 0 ? datesExclude : undefined,
          is_comparison_report: comparisonReportId != null ? true : undefined,
          comparison_report_id: comparisonReportId ?? undefined,
          as_background_task: asBackgroundTask,
        },
      },
    );
    return res.data;
  },

  /**
   * Poll a background-task result link (returned by `createDraft` when
   * `as_background_task` is set) until the task finishes. The link is an
   * absolute URL to `/api/tasks/{id}/result?token=…`; we route it through
   * apiClient (proxy-aware) by keeping just the path + query. Mirrors v1's
   * getBackgroundTaskResult (clinician/src/services/patient.ts). The report
   * PDF only exists once this resolves, so callers MUST await it before
   * refetching the reports list — otherwise the new report isn't there yet.
   */
  async waitForReportTask(
    link: string,
    opts?: { maxAttempts?: number; delayMs?: number },
  ): Promise<void> {
    const maxAttempts = opts?.maxAttempts ?? 90; // ~3 min at 2s
    const delayMs = opts?.delayMs ?? 2000;
    let path = link;
    try {
      const u = new URL(link);
      path = u.pathname + u.search;
    } catch {
      /* already a relative path */
    }
    path = path.replace(/^\/api(?=\/)/, '');
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const res = await apiClient.get<{ task_is_success?: boolean; task_is_error?: boolean }>(path);
      if (res.data?.task_is_success) return;
      if (res.data?.task_is_error) throw new Error('Report generation failed');
      await new Promise((r) => setTimeout(r, delayMs));
    }
    throw new Error('Report generation timed out');
  },

  /**
   * Email the patient a public link to view a generated report. Mirrors v1's
   * sendPatientReportLink (clinician/src/services/patient.ts:1359).
   * GET /reporting/patients/{pid}/patient_report/{rid}/send_patient_public_report_link
   */
  async sendReportLink(patientId: number, reportId: number): Promise<void> {
    await apiClient.get(
      `/reporting/patients/${patientId}/patient_report/${reportId}/send_patient_public_report_link`,
      { params: { to_patient: true, to_admin: false } },
    );
  },

  /**
   * GET /reporting/patient/{patient_id}/patient_reports?date_since&date_until
   * (MNT swagger:10349). v1 fetches a wide ±2-year window to surface every
   * report (clinician/src/services/patient.ts:938). We do the same so the
   * tab shows everything historical without a date filter UI.
   */
  async list(patientId: number, dateSince: string, dateUntil: string): Promise<PatientReport[]> {
    try {
      const res = await apiClient.get<PatientReport[]>(
        `/reporting/patient/${patientId}/patient_reports`,
        { params: { date_since: dateSince, date_until: dateUntil } },
      );
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return [];
      throw err;
    }
  },

  /**
   * GET /reporting/patient_report/{id}/pdf as binary (swagger:10521). The
   * response is fetched as a Blob; the caller wraps it in `URL.createObjectURL`
   * so it can be embedded inline via `<object>`. This bypasses the
   * `Content-Disposition: attachment` header the secure-link URL uses (which
   * forces a download when loaded into an iframe). Mirrors v1's
   * `getPatientReportPdf` (clinician/src/services/patient.ts:99).
   */
  async pdfBlob(reportId: number): Promise<Blob> {
    const res = await apiClient.get<Blob>(`/reporting/patient_report/${reportId}/pdf`, {
      responseType: 'blob',
    });
    return res.data;
  },

  /**
   * Per-meal food-group breakdown for the patient's average plate over the
   * date range. Drives v1's Summary page "Average Plate Summary" — a 4-up
   * grid of donuts for breakfast/lunch/dinner/snack with vegetable/grains/
   * fruit/protein percentages.
   *
   * GET /patients/{id}/average_plate (MNT swagger:5848).
   */
  async averagePlate(patientId: number, params: { date_since: string; date_until: string }): Promise<AveragePlateData | null> {
    try {
      const res = await apiClient.get<AveragePlateData>(`/patients/${patientId}/average_plate`, {
        params: { date_since: params.date_since, date_until: params.date_until },
      });
      return res.data;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return null;
      throw err;
    }
  },
};
